import React, { useState, useCallback, useEffect } from 'react';
import { showToast } from '@/components/ui/Toast';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, X, Download, Trash2, Check, Home, Plus, Sparkles, Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAiAction } from '@/hooks/useAiAction';
import { getDocs, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase';
import { useDocumentVault } from '../hooks/useDocumentVault';
import { AppState, HomeInventoryItem } from '../types';

interface DocumentVaultProps {
  state: AppState;
  dispatch: React.Dispatch<any>;
}

const TAG_OPTIONS = ['Warranty', 'Lease', 'Medical', 'Insurance', 'Vehicle', 'Other'];

const INVENTORY_ROOMS = ['Living Room', 'Kitchen', 'Master Bedroom', 'Guest Bedroom', 'Bathroom', 'Garage', 'Office', 'Other'];

export const DocumentVault: React.FC<DocumentVaultProps> = ({ state, dispatch }) => {
  const { t } = useTranslation();
  const { documents, loading, uploadProgress, upload, deleteDocument } =
    useDocumentVault(state.currentHouseholdId);

  const [activeView, setActiveView] = useState<'documents' | 'inventory'>('documents');
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<HomeInventoryItem[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [showAddInventory, setShowAddInventory] = useState(false);
  const [itemRoom, setItemRoom] = useState('Living Room');
  const [itemName, setItemName] = useState('');
  const [itemValue, setItemValue] = useState('');
  const [itemPurchaseDate, setItemPurchaseDate] = useState('');
  const [itemSerial, setItemSerial] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [isSavingItem, setIsSavingItem] = useState(false);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'application/pdf' || file.type.startsWith('image/'))) {
      setSelectedFile(file);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      await upload(selectedFile, selectedTags);
      setSelectedFile(null);
      setSelectedTags([]);
      setShowUpload(false);
    } catch (error) {
      console.error('Upload failed:', error);
      showToast('Upload failed: ' + (error as Error).message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const { callAction: callAi } = useAiAction(state.currentHouseholdId);
  const [docInsights, setDocInsights] = useState<Record<string, { category?: string; summary?: string }>>({});
  const [analyzingDocId, setAnalyzingDocId] = useState<string | null>(null);

  const handleAnalyzeDoc = async (doc: any, action: 'classifyDocument' | 'summarizeDocument') => {
    setAnalyzingDocId(doc.id);
    const result = await callAi<{ category?: string; summary?: string }>(action, { documentName: doc.name, tags: doc.tags });
    if (result) setDocInsights(prev => ({ ...prev, [doc.id]: { ...prev[doc.id], ...result } }));
    setAnalyzingDocId(null);
  };

  const handleDelete = async (doc: any) => {
    try {
      await deleteDocument(doc);
      setConfirmDeleteId(null);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const filteredDocuments = activeFilter
    ? documents.filter(d => d.tags.includes(activeFilter))
    : documents;

  useEffect(() => {
    if (activeView !== 'inventory' || !state.currentHouseholdId) return;
    setInventoryLoading(true);
    getDocs(collection(db, 'households', state.currentHouseholdId, 'homeInventory'))
      .then(snap => setInventoryItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }))))
      .finally(() => setInventoryLoading(false));
  }, [activeView, state.currentHouseholdId]);

  const handleAddInventoryItem = async () => {
    if (!itemName || !itemValue || !state.currentHouseholdId || !state.currentUser) return;
    setIsSavingItem(true);
    try {
      await addDoc(collection(db, 'households', state.currentHouseholdId, 'homeInventory'), {
        room: itemRoom, name: itemName, estimatedValue: parseFloat(itemValue) || 0,
        purchaseDate: itemPurchaseDate || null, serialNumber: itemSerial || null,
        notes: itemNotes || null, householdId: state.currentHouseholdId,
        addedBy: state.currentUser.id, createdAt: serverTimestamp(),
      });
      setItemName(''); setItemValue(''); setItemPurchaseDate(''); setItemSerial(''); setItemNotes('');
      setShowAddInventory(false);
      const snap = await getDocs(collection(db, 'households', state.currentHouseholdId, 'homeInventory'));
      setInventoryItems(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })));
    } finally {
      setIsSavingItem(false);
    }
  };

  const inventoryTotalValue = inventoryItems.reduce((s, i) => s + (i.estimatedValue || 0), 0);
  const inventoryByRoom = INVENTORY_ROOMS.reduce((acc, room) => {
    const items = inventoryItems.filter(i => i.room === room);
    if (items.length > 0) acc[room] = items;
    return acc;
  }, {} as Record<string, HomeInventoryItem[]>);

  const formatSize = (bytes: number) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-slate-400">{t('documents.subtitle', 'Securely store and organize household documents')}</p>
        </div>
        {activeView === 'documents' && (
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-white hover:bg-brand-hover"
          >
            <Upload size={18} />
            {t('documents.upload_document', 'Upload Document')}
          </button>
        )}
        {activeView === 'inventory' && (
          <button
            onClick={() => setShowAddInventory(true)}
            className="flex items-center gap-2 rounded-full bg-brand px-4 py-2 text-white hover:bg-brand-hover"
          >
            <Plus size={18} />
            {t('documents.add_item', 'Add Item')}
          </button>
        )}
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 bg-slate-800 p-1 rounded-xl w-fit">
        <button onClick={() => setActiveView('documents')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeView === 'documents' ? 'bg-white text-brand shadow' : 'text-slate-400 hover:text-white'}`}>
          <FileText className="w-3.5 h-3.5" /> {t('documents.tab_documents', 'Documents')}
        </button>
        <button onClick={() => setActiveView('inventory')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-all ${activeView === 'inventory' ? 'bg-white text-brand shadow' : 'text-slate-400 hover:text-white'}`}>
          <Home className="w-3.5 h-3.5" /> {t('documents.tab_inventory', 'Home Inventory')}
        </button>
      </div>

      {/* Inventory View */}
      {activeView === 'inventory' && (
        <div className="space-y-6">
          {inventoryItems.length > 0 && (
            <div className="flex items-center gap-3 p-4 bg-blue-900/20 border border-blue-800 rounded-2xl">
              <span className="text-slate-400 text-sm">{t('documents.total_value', 'Total Estimated Value')}</span>
              <span className="text-2xl font-bold text-white ml-auto">${inventoryTotalValue.toLocaleString()}</span>
            </div>
          )}
          {inventoryLoading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-slate-800 rounded-2xl animate-pulse" />)}</div>
          ) : inventoryItems.length === 0 ? (
            <EmptyState
              icon={<Home className="w-8 h-8 text-slate-500" />}
              title={t('documents.inventory_empty_title', 'No inventory items yet')}
              description={t('documents.inventory_empty_desc', 'Document your belongings for insurance purposes.')}
            />
          ) : (
            <div className="space-y-6">
              {Object.entries(inventoryByRoom).map(([room, items]) => (
                <div key={room}>
                  <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">{room} <span className="text-slate-600">({items.length})</span></h3>
                  <div className="space-y-2">
                    {items.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-slate-900 rounded-2xl border border-slate-800">
                        <div>
                          <p className="text-white font-medium text-sm">{item.name}</p>
                          {item.purchaseDate && <p className="text-slate-500 text-xs">Purchased {item.purchaseDate}</p>}
                        </div>
                        <p className="text-white font-bold">${(item.estimatedValue || 0).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Add Inventory Modal */}
          {showAddInventory && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center">
              <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-md" onClick={() => setShowAddInventory(false)} />
              <div className="relative bg-slate-950 rounded-[2rem] p-8 w-full max-w-lg shadow-2xl space-y-4 mx-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-black uppercase tracking-widest text-white">{t('documents.add_inventory_item', 'Add Inventory Item')}</h3>
                  <button onClick={() => setShowAddInventory(false)} className="p-2 hover:bg-slate-800 rounded-xl"><X className="w-4 h-4 text-slate-400" /></button>
                </div>
                <div>
                  <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 block">Room</label>
                  <select value={itemRoom} onChange={e => setItemRoom(e.target.value)} className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500">
                    {INVENTORY_ROOMS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                {[
                  { label: 'Item Name *', value: itemName, set: setItemName, placeholder: 'e.g. 65" Samsung TV' },
                  { label: 'Estimated Value ($) *', value: itemValue, set: setItemValue, placeholder: '0.00', type: 'number' },
                  { label: 'Purchase Date', value: itemPurchaseDate, set: setItemPurchaseDate, placeholder: 'YYYY-MM-DD' },
                  { label: 'Serial Number', value: itemSerial, set: setItemSerial, placeholder: 'Optional' },
                  { label: 'Notes', value: itemNotes, set: setItemNotes, placeholder: 'Optional' },
                ].map(({ label, value, set, placeholder, type }) => (
                  <div key={label}>
                    <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1 block">{label}</label>
                    <input type={type || 'text'} value={value} onChange={e => set(e.target.value)} placeholder={placeholder} className="w-full px-4 py-3 rounded-xl bg-slate-900 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500" />
                  </div>
                ))}
                <button onClick={handleAddInventoryItem} disabled={!itemName || !itemValue || isSavingItem} className="w-full py-3 bg-brand text-white font-black text-2xs uppercase tracking-widest rounded-xl hover:bg-brand-hover disabled:opacity-50 transition-colors">
                  {isSavingItem ? t('common.saving', 'Saving...') : t('documents.add_item', 'Add Item')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents View */}
      {activeView === 'documents' && <div className="space-y-6">
      {/* Filter Tags */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveFilter(null)}
          className={`rounded-full px-4 py-2 text-sm font-medium ${
            activeFilter === null
              ? 'bg-brand text-white'
              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
          }`}
        >
          {t('common.all', 'All')}
        </button>
        {TAG_OPTIONS.map(tag => (
          <button
            key={tag}
            onClick={() => setActiveFilter(tag)}
            className={`rounded-full px-4 py-2 text-sm font-medium ${
              activeFilter === tag
                ? 'bg-brand text-white'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-800 p-6">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">{t('documents.upload_document', 'Upload Document')}</h2>
              <button
                onClick={() => setShowUpload(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Drag and Drop */}
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input')?.click()}
              className="mb-4 cursor-pointer rounded-xl border-2 border-dashed border-slate-700 p-8 text-center transition-colors hover:border-brand"
            >
              <Upload className="mx-auto mb-3 text-slate-500" size={48} />
              <p className="text-slate-300">{t('documents.drag_drop', 'Drag & drop or click to select')}</p>
              <p className="text-sm text-slate-500">{t('documents.file_hint', 'PDF or images up to 10MB')}</p>
              <input
                id="file-input"
                type="file"
                accept="application/pdf,image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Selected File */}
            {selectedFile && (
              <div className="mb-6 rounded-lg bg-slate-800 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">{selectedFile.name}</p>
                    <p className="text-sm text-slate-400">
                      {formatSize(selectedFile.size)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="rounded-lg p-1 text-slate-400 hover:bg-slate-700 hover:text-white"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Tags */}
            <div className="mb-6">
              <p className="mb-3 text-sm font-medium text-slate-300">{t('documents.tags', 'Tags')}</p>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map(tag => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`rounded-full px-3 py-1.5 text-sm ${
                      selectedTags.includes(tag)
                        ? 'bg-brand text-white'
                        : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Progress Bar */}
            {uploadProgress > 0 && (
              <div className="mb-4">
                <div className="h-2 w-full rounded-full bg-slate-800">
                  <div
                    className="h-full rounded-full bg-blue-500 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="mt-1 text-right text-sm text-slate-400">
                  {uploadProgress.toFixed(0)}%
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowUpload(false)}
                className="flex-1 rounded-lg bg-slate-800 px-4 py-2 text-slate-300 hover:bg-slate-700"
                disabled={isUploading}
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="flex-1 rounded-full bg-brand px-4 py-2 text-white hover:bg-brand-hover disabled:opacity-50"
              >
                {isUploading ? t('documents.uploading', 'Uploading...') : t('documents.upload_document', 'Upload')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className="animate-pulse rounded-2xl bg-slate-900 border border-slate-800 p-4"
            >
              <div className="mb-4 h-6 rounded bg-slate-800" />
              <div className="mb-2 h-4 w-32 rounded bg-slate-800" />
              <div className="h-4 w-24 rounded bg-slate-800" />
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredDocuments.length === 0 && (
        <div className="rounded-2xl bg-slate-900 border border-slate-800 p-12 text-center">
          <FileText className="mx-auto mb-4 text-slate-600" size={64} />
          <h3 className="mb-2 text-xl font-semibold text-white">{t('documents.empty_title', 'No documents yet')}</h3>
          <p className="text-slate-400">
            {activeFilter
              ? t('documents.empty_filtered', 'No documents with "{{tag}}" tag', { tag: activeFilter })
              : t('documents.empty_desc', 'Upload your first document to get started')}
          </p>
        </div>
      )}

      {/* Document Grid */}
      {!loading && filteredDocuments.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredDocuments.map(doc => (
            <div
              key={doc.id}
              className="rounded-2xl bg-slate-900 border border-slate-800 p-4 transition-colors hover:border-slate-700"
            >
              <div className="mb-4 flex items-start justify-between">
                <FileText className="text-blue-400" size={32} />
                {confirmDeleteId === doc.id ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white"
                    >
                      <X size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      className="rounded-lg bg-red-600 p-1.5 text-white hover:bg-red-700"
                    >
                      <Check size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(doc.id)}
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-red-400"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <h3 className="mb-2 truncate text-lg font-semibold text-white">
                {doc.name}
              </h3>

              <div className="mb-4 space-y-1 text-sm">
                <p className="text-slate-400">{formatSize(doc.size)}</p>
                <p className="text-slate-500">
                  {doc.uploadedAt?.toDate?.()?.toLocaleDateString() ?? 'Unknown'}
                </p>
              </div>

              <div className="mb-4 flex flex-wrap gap-1">
                {doc.tags.map(tag => (
                  <span
                    key={tag}
                    className="rounded-full bg-slate-800 px-2 py-1 text-xs text-slate-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {docInsights[doc.id] && (
                <div className="mb-3 space-y-1 p-3 bg-blue-900/20 rounded-xl border border-blue-800/30">
                  {docInsights[doc.id].category && (
                    <p className="text-[9px] font-black uppercase tracking-widest text-blue-400">
                      {t('documents.category_label', 'Category:')} {docInsights[doc.id].category}
                    </p>
                  )}
                  {docInsights[doc.id].summary && (
                    <p className="text-2xs text-slate-400 leading-snug">{docInsights[doc.id].summary}</p>
                  )}
                </div>
              )}
              <div className="flex items-center gap-1.5 mb-3">
                <button
                  onClick={() => handleAnalyzeDoc(doc, 'classifyDocument')}
                  disabled={analyzingDocId === doc.id}
                  className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-black uppercase rounded-lg disabled:opacity-50 transition-colors"
                >
                  {analyzingDocId === doc.id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                  {t('documents.classify', 'Classify')}
                </button>
                <button
                  onClick={() => handleAnalyzeDoc(doc, 'summarizeDocument')}
                  disabled={analyzingDocId === doc.id}
                  className="flex items-center gap-1 px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[9px] font-black uppercase rounded-lg disabled:opacity-50 transition-colors"
                >
                  {analyzingDocId === doc.id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                  {t('documents.summarize', 'Summarize')}
                </button>
              </div>
              <div className="flex items-center justify-between">
                <a
                  href={doc.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                >
                  <Download size={16} />
                  {t('documents.download', 'Download')}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>}
    </div>
  );
}